import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Button from '../base/Button';
import Card from '../base/Card';
import { supabase } from '../../utils/supabase';
import { DEFAULT_FOOTER_SETTINGS, type FooterSettings } from './SiteFooter';

const fieldGroups: Array<{
  title: string;
  fields: Array<{ key: keyof FooterSettings; label: string; textarea?: boolean }>;
}> = [
  {
    title: 'Copyright',
    fields: [
      { key: 'copyrightTitle', label: 'Section title' },
      { key: 'copyrightText', label: 'Copyright text' },
      { key: 'description', label: 'Website description', textarea: true },
      { key: 'lastUpdated', label: 'Last updated' },
      { key: 'launchDate', label: 'Website launch date (YYYY-MM-DD)' },
    ],
  },
  {
    title: 'Advance Notice',
    fields: [
      { key: 'noticeTitle', label: 'Section title' },
      { key: 'noticeText', label: 'Highlighted notice', textarea: true },
      { key: 'noticeSubtext', label: 'Secondary notice', textarea: true },
    ],
  },
  {
    title: 'Contact & Visit Me',
    fields: [
      { key: 'contactTitle', label: 'Section title' },
      { key: 'email', label: 'Email' },
      { key: 'githubLabel', label: 'GitHub label' },
      { key: 'githubUrl', label: 'GitHub URL' },
      { key: 'homepageLabel', label: 'Homepage label' },
      { key: 'homepageUrl', label: 'Homepage / QR URL' },
    ],
  },
  {
    title: 'Popular & Important',
    fields: [
      { key: 'popularTitle', label: 'Section title' },
      { key: 'popularText', label: 'QR code text', textarea: true },
      { key: 'badgeText', label: 'Round badge text' },
    ],
  },
];

export default function FooterSettingsPanel() {
  const location = useLocation();
  const [adminTokenPresent, setAdminTokenPresent] = useState(false);
  const [authSessionPresent, setAuthSessionPresent] = useState(false);
  const [authEmail, setAuthEmail] = useState('qiuyating.888@163.com');
  const [authPassword, setAuthPassword] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [settings, setSettings] = useState<FooterSettings>(DEFAULT_FOOTER_SETTINGS);
  const [status, setStatus] = useState<'idle' | 'loading' | 'saving' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');

  useEffect(() => {
    const checkAdminToken = () => {
      setAdminTokenPresent(Boolean(localStorage.getItem('admin_token')));
    };

    checkAdminToken();
    const timer = window.setInterval(checkAdminToken, 1000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!supabase) return;

    supabase.auth.getSession().then(({ data }) => {
      setAuthSessionPresent(Boolean(data.session));
    });

    const { data: listener } = supabase.auth.onAuthStateChange((_event, session) => {
      setAuthSessionPresent(Boolean(session));
    });

    return () => {
      listener.subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (!isOpen || !authSessionPresent || !supabase) return;

    const loadSettings = async () => {
      setStatus('loading');
      const { data, error } = await supabase
        .from('footer_settings')
        .select('settings')
        .eq('id', 1)
        .maybeSingle();

      if (error) {
        setStatus('error');
        setMessage('Could not load footer settings. Please run the Supabase SQL setup first.');
        return;
      }

      if (data?.settings && typeof data.settings === 'object') {
        setSettings({ ...DEFAULT_FOOTER_SETTINGS, ...(data.settings as Partial<FooterSettings>) });
      }
      setStatus('idle');
      setMessage('');
    };

    void loadSettings();
  }, [authSessionPresent, isOpen]);

  if (!location.pathname.startsWith('/admin') || !adminTokenPresent) {
    return null;
  }

  const updateField = (key: keyof FooterSettings, value: string | boolean) => {
    setSettings((current) => ({ ...current, [key]: value }));
  };

  const signInToSupabase = async () => {
    if (!supabase) {
      setStatus('error');
      setMessage('Supabase is not connected.');
      return;
    }

    setStatus('loading');
    const { error } = await supabase.auth.signInWithPassword({
      email: authEmail,
      password: authPassword,
    });

    if (error) {
      setStatus('error');
      setMessage('Supabase Auth login failed. Check the email and password.');
      return;
    }

    setStatus('success');
    setMessage('Secure edit permission connected.');
    setAuthPassword('');
  };

  const signOutFromSupabase = async () => {
    await supabase?.auth.signOut();
    setAuthSessionPresent(false);
    setMessage('Secure edit permission disconnected.');
  };

  const saveSettings = async () => {
    if (!supabase) {
      setStatus('error');
      setMessage('Supabase is not connected.');
      return;
    }
    if (!authSessionPresent) {
      setStatus('error');
      setMessage('Log in with the authorized Supabase Auth account before saving.');
      return;
    }

    setStatus('saving');
    const { error } = await supabase
      .from('footer_settings')
      .upsert({ id: 1, settings, updated_at: new Date().toISOString() }, { onConflict: 'id' });

    if (error) {
      setStatus('error');
      setMessage('Save failed. Confirm the SQL setup was run and the logged-in email is authorized.');
      return;
    }

    setStatus('success');
    setMessage('Footer settings saved. Refresh the public site to see the update.');
    setTimeout(() => setStatus('idle'), 3000);
  };

  return (
    <>
      <button
        type="button"
        className="fixed bottom-6 right-6 z-[90] rounded-full bg-sky-600 px-5 py-3 text-sm font-medium text-white shadow-lg hover:bg-sky-700"
        onClick={() => setIsOpen(true)}
      >
        Edit Footer
      </button>

      {isOpen && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/50 p-4">
          <Card className="max-h-[88vh] w-full max-w-4xl overflow-hidden bg-white" glass={false}>
            <div className="flex items-center justify-between border-b px-6 py-4">
              <div>
                <h2 className="text-xl font-bold text-gray-900">Footer Settings</h2>
                <p className="text-sm text-gray-500">Edit the bottom copyright, contact, notice, and QR sections.</p>
              </div>
              <button
                type="button"
                className="rounded-full p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-800"
                onClick={() => setIsOpen(false)}
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>

            <div className="max-h-[62vh] overflow-y-auto px-6 py-5">
              {!authSessionPresent ? (
                <div className="mx-auto max-w-md rounded-xl border border-sky-100 bg-sky-50 p-5">
                  <h3 className="mb-2 font-semibold text-gray-900">Connect secure edit permission</h3>
                  <p className="mb-4 text-sm text-gray-600">
                    Footer saving is protected by Supabase Auth and Row Level Security.
                  </p>
                  <div className="space-y-3">
                    <label className="block">
                      <span className="mb-1 block text-sm font-medium text-gray-700">Authorized email</span>
                      <input
                        type="email"
                        value={authEmail}
                        onChange={(event) => setAuthEmail(event.target.value)}
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-sky-500"
                      />
                    </label>
                    <label className="block">
                      <span className="mb-1 block text-sm font-medium text-gray-700">Supabase Auth password</span>
                      <input
                        type="password"
                        value={authPassword}
                        onChange={(event) => setAuthPassword(event.target.value)}
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-sky-500"
                      />
                    </label>
                    <Button variant="primary" onClick={signInToSupabase} disabled={status === 'loading'}>
                      {status === 'loading' ? 'Signing in...' : 'Sign in and edit'}
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="grid gap-6 md:grid-cols-2">
                    {fieldGroups.map((group) => (
                      <section key={group.title} className="rounded-xl border border-gray-200 p-4">
                        <h3 className="mb-4 font-semibold text-gray-900">{group.title}</h3>
                        <div className="space-y-4">
                          {group.fields.map((field) => (
                            <label key={field.key} className="block">
                              <span className="mb-1 block text-sm font-medium text-gray-700">{field.label}</span>
                              {field.textarea ? (
                                <textarea
                                  value={String(settings[field.key] ?? '')}
                                  onChange={(event) => updateField(field.key, event.target.value)}
                                  className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-sky-500"
                                  rows={3}
                                />
                              ) : (
                                <input
                                  type="text"
                                  value={String(settings[field.key] ?? '')}
                                  onChange={(event) => updateField(field.key, event.target.value)}
                                  className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-sky-500"
                                />
                              )}
                            </label>
                          ))}
                          {group.title === 'Popular & Important' && (
                            <label className="flex items-center gap-2 text-sm text-gray-700">
                              <input
                                type="checkbox"
                                checked={settings.showBadge}
                                onChange={(event) => updateField('showBadge', event.target.checked)}
                                className="h-4 w-4 rounded border-gray-300 text-sky-600 focus:ring-sky-500"
                              />
                              Show round badge
                            </label>
                          )}
                        </div>
                      </section>
                    ))}
                  </div>
                  <button
                    type="button"
                    className="mt-4 text-sm text-gray-500 hover:text-gray-800"
                    onClick={signOutFromSupabase}
                  >
                    Disconnect secure edit permission
                  </button>
                </>
              )}
            </div>

            <div className="flex items-center justify-between border-t px-6 py-4">
              <p className={`text-sm ${status === 'error' ? 'text-red-600' : 'text-green-600'}`}>
                {message}
              </p>
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setSettings(DEFAULT_FOOTER_SETTINGS)}>
                  Restore defaults
                </Button>
                <Button variant="primary" onClick={saveSettings} disabled={status === 'saving' || status === 'loading'}>
                  {status === 'saving' ? 'Saving...' : 'Save settings'}
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}
    </>
  );
}
